import React from "react";

interface MobileChartReadoutProps {
  /** What the pressed mark is, e.g. `Aug` — `null` while nothing is pressed. */
  label?: string | null;
  /** That mark's figure, already written out: `₹28,200`, `3 new clients`. */
  value?: string | null;
  /** The colour the pressed mark is drawn in, for the swatch beside the
   *  figure — identity is never carried by the plot's colour alone. */
  swatch: string;
  /** What the line says while nothing is pressed. A measured fact about the
   *  series rather than a hint about how to use it — see `EarningsHero`. */
  fallback?: React.ReactNode;
}

/**
 * The line above a phone's chart that says what was just pressed.
 *
 * What a tooltip is for, in a place a thumb is not covering. A phone has no
 * pointer to hover with, so the dashboard's charts are pressed rather than
 * hovered, and a floating panel anchored to the press would open under the
 * finger that asked for it. This is one line, right-aligned over the plot, at a
 * fixed height so pressing a mark never moves the plot under the finger.
 *
 * Shared by the two charts a phone gets — the earnings bars and the new-clients
 * trend — so the same gesture reads the same way on both. `aria-live` because it
 * is the answer to a press: each mark's own button carries its figure in its
 * label, so a reader arriving by keyboard hears it either way, and this is for
 * the reader looking at it.
 */
const MobileChartReadout: React.FC<MobileChartReadoutProps> = ({
  label = null,
  value = null,
  swatch,
  fallback = null,
}) => (
  <p
    aria-live="polite"
    className="h-[18px] flex items-center justify-end gap-1.5 text-[12px] leading-none"
  >
    {label && value ? (
      <>
        <span
          aria-hidden
          className="w-2 h-2 rounded-sm"
          style={{ backgroundColor: swatch }}
        />
        <span className="text-ink-muted">{label}</span>
        <span className="font-semibold text-ink">{value}</span>
      </>
    ) : (
      fallback
    )}
  </p>
);

export default MobileChartReadout;
